import React from 'react';

// Reusable Legend component for different map variables
export interface LegendConfig {
  title: string;
  dataYear: string;
  colorScale: string[];
  breaks: number[];
  unit: string;
  tractCount: number;
  referenceValue?: { label: string; value: number };
}

const MapLegend: React.FC<LegendConfig> = ({
  title, 
  dataYear, 
  colorScale, 
  breaks, 
  unit, 
  tractCount, 
  referenceValue 
}) => {
  // For carbon footprint, show unit only in the header, not on every value
  const showUnitInTitleOnly = unit === ' MT CO₂e/HH';

  // Helper function to format political lean as "D +15" or "R +2"
  const formatPoliticalLean = (value: number): string => {
    if (value > 0) {
      return `D +${value.toFixed(1)}`;
    } else if (value < 0) {
      return `R +${Math.abs(value).toFixed(1)}`;
    } else {
      return 'Even';
    }
  };

  // Generate legend items dynamically based on breaks and colors
  const legendItems = breaks.slice(0, -1).map((breakValue, index) => {
    const nextBreak = breaks[index + 1];
    const color = colorScale[index];
    
    // Format numbers based on unit type
    const formatValue = (value: number) => {
      if (unit === ' MT CO₂e/HH') {
        return value.toFixed(1); // One decimal place for carbon footprint
      } else if (title === 'Political Lean') {
        // Special formatting for political lean only
        return formatPoliticalLean(value);
      } else {
        // For empty units or other units, use standard number formatting
        return value.toLocaleString();
      }
    };
    
    const unitSuffix = !showUnitInTitleOnly && unit ? unit : '';
    let label = '';
    if (title === 'Political Lean') {
      // Special formatting for political lean legend only
      if (index === 0) {
        label = `< ${formatValue(breakValue)}`;
      } else if (index === breaks.length - 2) {
        label = `${formatValue(breakValue)}+`;
      } else {
        label = `${formatValue(breakValue)} - ${formatValue(nextBreak)}`;
      }
      } else {
        // Standard formatting for other variables (including those with empty units)
        if (index === 0) {
          label = `Under ${formatValue(breakValue)}${unitSuffix}`;
        } else if (index === breaks.length - 2) {
          label = `${formatValue(breakValue)}${unitSuffix}+`;
        } else {
          label = `${formatValue(breakValue)}${unitSuffix} - ${formatValue(nextBreak)}${unitSuffix}`;
        }
      }
    
    return { color, label };
  });

  return (
    <div 
      className="bg-white p-2 rounded shadow-sm border" 
      style={{ 
        minWidth: '160px',
        maxWidth: '190px',
        position: 'relative',
        zIndex: 50, /* Lower than navigation z-index: 100 */
        display: 'block',
        visibility: 'visible',
        opacity: 1
      }}
    >
        <div className="mb-2">
          <h4 className="text-sm font-semibold text-gray-800">
            {title}
            {showUnitInTitleOnly && ` (${unit.trim()})`}
          </h4>
          <p className="text-xs text-gray-600">{dataYear}</p>
        </div>
        
        {/* Color Scale with Labels */}
        <div className="mb-2">
          <div className="space-y-1">
            {legendItems.map((item, index) => (
              <div key={index} className="flex items-center">
                <div 
                  className="mr-2"
                  style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: item.color,
                    border: '1px solid #ccc',
                    borderRadius: '2px',
                    display: 'inline-block'
                  }}
                ></div>
                <span className="text-xs text-gray-700">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Additional Info */}
        <div className="pt-2 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            {referenceValue && (
              <div>
                {referenceValue.label}: {
                  title === 'Political Lean'
                    ? (referenceValue.value > 0 
                        ? `D +${referenceValue.value.toFixed(1)}`
                        : referenceValue.value < 0
                        ? `R +${Math.abs(referenceValue.value).toFixed(1)}`
                        : 'Even')
                    : unit === ' min'
                    ? `${referenceValue.value}${unit}`
                    : unit === ' MT CO₂e/HH'
                    ? `${referenceValue.value.toFixed(1)}${unit}`
                    : unit === '%'
                    ? `${referenceValue.value.toFixed(1)}${unit}`
                    : unit === ''
                    ? `${referenceValue.value.toLocaleString()}`
                    : `${referenceValue.value.toLocaleString()}${unit}`
              }
              </div>
            )}
            <div>{tractCount.toLocaleString()} census tracts</div>
            <div className="mt-1 pt-1 border-t border-gray-200">
              <div className="flex items-center">
                <div 
                  className="mr-2"
                  style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#cccccc',
                    border: '1px solid #999',
                    borderRadius: '2px',
                    display: 'inline-block'
                  }}
                ></div>
                <span className="text-xs text-gray-500">Gray = No data</span>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default MapLegend;
